import React, { useState, useEffect, useCallback } from 'react'
import { 
  ArrowLeft,
  Plus,
  Save,
  RotateCcw,
  Building,
  MapPin,
  Phone,
  CreditCard,
  FileText,
  Upload
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import { useNavigate } from 'react-router-dom'
import companyService from '../../services/companyService'
import fileUploadService from '../../services/fileUploadService'
import { toast } from 'react-toastify'
import { getStateOptions } from '../../constants/indianStates'

const CompanyMaster = () => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('companyInfo')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState({
    // Company Info
    companyName: '',
    constitutionOfBusiness: '',
    corporateIdentityNo: '',
    dateOfIncorporation: '',
    panNo: '',
    gstClassification: '',
    gstin: '',
    msmeCategory: '',
    udyamRegistrationNo: '',
    ieCode: '',
    tan: '',
    companyType: ''
  })

  const [addresses, setAddresses] = useState([
    {
      id: 1,
      country: '',
      stateProvince: '',
      cityDistrict: '',
      pinCode: '',
      addressLine1: '',
      addressLine2: '',
      addressLine3: '',
      addressLine4: ''
    }
  ])

  const [contacts, setContacts] = useState([
    {
      id: 1,
      contactPerson: '',
      email: '',
      mobile: '',
      alternatePhone: '',
      department: '',
      designation: ''
    }
  ])

  const [bankDetails, setBankDetails] = useState([
    {
      id: 1,
      beneficiaryName: 'IDMS INFOTECH PRIVATE LIMI',
      bankName: 'ICICI',
      bankBranch: 'RAM MARUTI ROAD, THANE',
      bankAddress: '',
      accountType: 'Current',
      accountNo: '003505016786',
      ifscCode: 'ICIC0000035',
      micrCode: '',
      exports: 'No',
      swiftCode: '',
      intermediaryBank: '',
      intermediaryBankSwiftCode: ''
    }
  ])

  const [templateData, setTemplateData] = useState({
    purchaseOrderDomestics: 'PO Domestic',
    purchaseOrderImports: 'Regular POI',
    proformaInvoiceDomestic: 'PI Domestic',
    proformaInvoiceExports: 'PI Exports',
    taxInvoiceDomestics: 'Turnover less than 5 CR',
    taxInvoiceExports: 'Turnover less than 5 CR'
  })

  const [documentData, setDocumentData] = useState({
    companyLogo: '',
    companyWelcomeInfo: '',
    companySOSignature: '',
    companyPOSignature: '',
    companySOPDFHeader: '',
    companyPOPDFHeader: ''
  })

  // Load company data on component mount
  const loadCompanyData = useCallback(async () => {
    try {
      setLoading(true)
      const response = await companyService.getCompanyDetails()
      
      if (response.success) {
        const company = response.data
        
        // Update form data with fetched company data
        setFormData({
          companyName: company.companyName || '',
          constitutionOfBusiness: company.constitutionOfBusiness || '',
          corporateIdentityNo: company.companyCIN || '',
          dateOfIncorporation: company.dateOfIncorporation ? 
            new Date(company.dateOfIncorporation).toISOString().split('T')[0] : '',
          panNo: company.companyPAN || '',
          gstClassification: company.GSTClassification || '',
          gstin: company.GSTIN || '',
          msmeCategory: company.companyMSMENo || '',
          udyamRegistrationNo: company.udyamRegistrationNo || '',
          ieCode: company.importExportIECode || '',
          tan: company.TANForTDSTCS || '',
          companyType: company.companyType || ''
        })

        // Update addresses if available
        if (company.companyBillingAddress) {
          setAddresses([{
            id: 1,
            country: company.companyBillingAddress.country || '',
            stateProvince: company.companyBillingAddress.state || '',
            cityDistrict: company.companyBillingAddress.city || '',
            pinCode: company.companyBillingAddress.pinCode || '',
            addressLine1: company.companyBillingAddress.addressLine1 || '',
            addressLine2: company.companyBillingAddress.addressLine2 || '',
            addressLine3: company.companyBillingAddress.addressLine3 || '',
            addressLine4: company.companyBillingAddress.addressLine4 || ''
          }])
        }

        // Update contacts if available
        if (company.contactInfo && company.contactInfo.length > 0) {
          setContacts(company.contactInfo.map((contact, index) => ({
            id: contact._id || `contact_${index + 1}`,
            contactPersonName: contact.contactPersonName || '',
            designation: contact.designation || '',
            department: contact.department || '',
            mobile: contact.companyContactPersonNumber || '',
            alternatePhone: '',
            email: contact.companyContactPersonEmail || ''
          })))
        } else {
          // If no contacts, initialize with empty contact
          setContacts([{
            id: 1,
            contactPersonName: '',
            designation: '',
            department: '',
            mobile: '',
            alternatePhone: '',
            email: ''
          }])
        }

        // Update bank details if available
        if (company.companyBankName) {
          setBankDetails([{
            id: 1,
            beneficiaryName: company.companyBefName || '',
            bankName: company.companyBankName || '',
            bankBranch: company.companyBankBranch || '',
            bankAddress: '',
            accountType: company.companyAccType || '',
            accountNo: company.companyAccountNumber || '',
            ifscCode: company.companyBankIFSCCode || '',
            micrCode: '',
            exports: company.exports || 'No',
            swiftCode: '',
            intermediaryBank: '',
            intermediaryBankSwiftCode: ''
          }])
        }

        // Update template data if available
        setTemplateData({
          purchaseOrderDomestics: company.PODomesticTemplates || '',
          purchaseOrderImports: company.POImportsTemplates || '',
          proformaInvoiceDomestic: company.PIDomesticTemplates || '',
          proformaInvoiceExports: company.PIExportsTemplates || '',
          taxInvoiceDomestics: company.TIDomesticTemplates || '',
          taxInvoiceExports: company.TIExportsTemplates || ''
        })

        // Update document data if available
        setDocumentData({
          companyLogo: company.logo || '',
          companyWelcomeInfo: company.welcomeInfo || '',
          companySOSignature: company.SOSignature || '',
          companyPOSignature: company.companySignature || '',
          companySOPDFHeader: company.SOPdfHeader || '',
          companyPOPDFHeader: company.companyPdfHeader || ''
        })
      }
    } catch (error) {
      console.error('Error loading company data:', error)
      toast.error('Failed to load company details')
    } finally {
      setLoading(false)
    }
  }, [])

  // Load data on component mount
  useEffect(() => {
    loadCompanyData()
  }, [loadCompanyData])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleTemplateChange = (e) => {
    const { name, value } = e.target
    setTemplateData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleDocumentChange = (e) => {
    const { name, value } = e.target
    setDocumentData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleFileSelect = async (fieldName) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.svg,.png,.jpg,.jpeg,.pdf,.doc,.docx'
    input.style.display = 'none'
    
    input.onchange = async (e) => {
      const file = e.target.files[0]
      if (file) {
        // Validate file size (max 5MB)
        const maxSize = 5 * 1024 * 1024 // 5MB
        if (file.size > maxSize) {
          toast.error('File size must be less than 5MB')
          return
        }
        
        // Validate file type
        const allowedTypes = [
          'image/svg+xml',
          'image/png',
          'image/jpeg',
          'image/jpg',
          'application/pdf',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ]
        
        if (!allowedTypes.includes(file.type)) {
          toast.error('Please select a valid file type (SVG, PNG, JPG, PDF, DOC, DOCX)')
          return
        }
        
        try {
          // Upload file to server
          toast.info('Uploading file...')
          const response = await fileUploadService.uploadFile(file, fieldName)
          
          if (response.success) {
            // Update the document data with the uploaded file info
            setDocumentData(prev => ({
              ...prev,
              [fieldName]: response.data.filename
            }))
            
            toast.success(`File uploaded successfully: ${file.name}`)
            console.log(`File uploaded for ${fieldName}:`, response.data)
          } else {
            toast.error('Failed to upload file')
          }
        } catch (error) {
          console.error('File upload error:', error)
          toast.error('Error uploading file. Please try again.')
        }
      }
    }
    
    // Trigger the file input
    document.body.appendChild(input)
    input.click()
    document.body.removeChild(input)
  }

  const handleViewFile = async (fieldName) => {
    const fileName = documentData[fieldName]
    if (fileName) {
      try {
        toast.info('Loading file...')
        
        // Get the file using authenticated API
        const response = await fileUploadService.getFile(fileName)
        
        // Create blob URL and open in new tab
        const blob = new Blob([response.data])
        const url = window.URL.createObjectURL(blob)
        window.open(url, '_blank')
        
        // Clean up the URL after a delay
        setTimeout(() => {
          window.URL.revokeObjectURL(url)
        }, 1000)
        
        toast.success(`File opened: ${fileName}`)
        console.log(`View file for ${fieldName}:`, fileName)
      } catch (error) {
        console.error('Error viewing file:', error)
        if (error.response?.status === 401) {
          toast.error('Authentication required. Please log in again.')
        } else if (error.response?.status === 404) {
          toast.error('File not found. Please upload the file again.')
        } else {
          toast.error('Error opening file. Please try again.')
        }
      }
    } else {
      toast.warning('No file selected for this document')
    }
  }

  const handleAddressChange = (addressId, field, value) => {
    setAddresses(prev => 
      prev.map(addr => 
        addr.id === addressId 
          ? { ...addr, [field]: value }
          : addr
      )
    )
  }

  const addAddress = () => {
    const newAddress = {
      id: Date.now(),
      country: '',
      stateProvince: '',
      cityDistrict: '',
      pinCode: '',
      addressLine1: '',
      addressLine2: '',
      addressLine3: '',
      addressLine4: ''
    }
    setAddresses(prev => [...prev, newAddress])
  }

  const removeAddress = (addressId) => {
    if (addresses.length > 1) {
      setAddresses(prev => prev.filter(addr => addr.id !== addressId))
    }
  }

  const handleContactChange = (contactId, field, value) => {
    setContacts(prev => 
      prev.map(contact => 
        contact.id === contactId 
          ? { ...contact, [field]: value }
          : contact
      )
    )
  }

  const addContact = () => {
    const newContact = {
      id: Date.now(),
      contactPerson: '',
      email: '',
      mobile: '',
      alternatePhone: '',
      department: '',
      designation: ''
    }
    setContacts(prev => [...prev, newContact])
  }

  const removeContact = (contactId) => {
    if (contacts.length > 1) {
      setContacts(prev => prev.filter(contact => contact.id !== contactId))
    }
  }

  const handleBankChange = (bankId, field, value) => {
    setBankDetails(prev => 
      prev.map(bank => 
        bank.id === bankId 
          ? { ...bank, [field]: value }
          : bank
      )
    )
  }

  const addBank = () => {
    const newBank = {
      id: Date.now(),
      beneficiaryName: '',
      bankName: '',
      bankBranch: '',
      bankAddress: '',
      accountType: '',
      accountNo: '',
      ifscCode: '',
      micrCode: '',
      exports: 'No',
      swiftCode: '',
      intermediaryBank: '',
      intermediaryBankSwiftCode: ''
    }
    setBankDetails(prev => [...prev, newBank])
  }

  const removeBank = (bankId) => {
    if (bankDetails.length > 1) {
      setBankDetails(prev => prev.filter(bank => bank.id !== bankId))
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      
      // Map form data to database structure
      const saveData = {
        // Basic company info
        companyName: formData.companyName,
        constitutionOfBusiness: formData.constitutionOfBusiness,
        companyCIN: formData.corporateIdentityNo,
        dateOfIncorporation: formData.dateOfIncorporation,
        companyPAN: formData.panNo,
        GSTClassification: formData.gstClassification,
        GSTIN: formData.gstin,
        companyMSMENo: formData.msmeCategory,
        udyamRegistrationNo: formData.udyamRegistrationNo,
        importExportIECode: formData.ieCode,
        TANForTDSTCS: formData.tan,
        companyType: formData.companyType,
        
        // Billing address
        companyBillingAddress: addresses.length > 0 ? {
          addressType: 'Billing',
          addressLine1: addresses[0].addressLine1,
          addressLine2: addresses[0].addressLine2,
          addressLine3: addresses[0].addressLine3,
          addressLine4: addresses[0].addressLine4,
          city: addresses[0].cityDistrict,
          country: addresses[0].country,
          district: addresses[0].cityDistrict,
          pinCode: addresses[0].pinCode,
          state: addresses[0].stateProvince
        } : null,
        
        // Contact info
        contactInfo: contacts.filter(contact => 
          contact.contactPersonName || contact.mobile || contact.email
        ).map(contact => ({
          contactPersonName: contact.contactPersonName,
          designation: contact.designation,
          department: contact.department,
          companyContactPersonNumber: contact.mobile,
          companyContactPersonEmail: contact.email
        })),
        
        // Bank details
        companyBefName: bankDetails.length > 0 ? bankDetails[0].beneficiaryName : '',
        companyBankName: bankDetails.length > 0 ? bankDetails[0].bankName : '',
        companyAccountNumber: bankDetails.length > 0 ? bankDetails[0].accountNo : '',
        companyAccType: bankDetails.length > 0 ? bankDetails[0].accountType : '',
        companyBankIFSCCode: bankDetails.length > 0 ? bankDetails[0].ifscCode : '',
        companyBankBranch: bankDetails.length > 0 ? bankDetails[0].bankBranch : '',
        exports: bankDetails.length > 0 ? bankDetails[0].exports : 'No',
        
        // Template data
        PODomesticTemplates: templateData.purchaseOrderDomestics,
        POImportsTemplates: templateData.purchaseOrderImports,
        PIDomesticTemplates: templateData.proformaInvoiceDomestic,
        PIExportsTemplates: templateData.proformaInvoiceExports,
        TIDomesticTemplates: templateData.taxInvoiceDomestics,
        TIExportsTemplates: templateData.taxInvoiceExports,
        
        // Document data
        logo: documentData.companyLogo,
        welcomeInfo: documentData.companyWelcomeInfo,
        SOSignature: documentData.companySOSignature,
        companySignature: documentData.companyPOSignature,
        SOPdfHeader: documentData.companySOPDFHeader,
        companyPdfHeader: documentData.companyPOPDFHeader
      }

      const response = await companyService.updateCompanyDetails(saveData)
      
      if (response.success) {
        toast.success('Company details saved successfully!')
        navigate('/settings/global-master')
      } else {
        toast.error(response.message || 'Failed to save company details')
      }
    } catch (error) {
      console.error('Error saving company data:', error)
      toast.error('Failed to save company details')
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    setFormData({
      companyName: 'IDMS Infotech Private Limited',
      constitutionOfBusiness: 'Private Limited',
      corporateIdentityNo: 'U72900MH2021PTC357360',
      dateOfIncorporation: '2021-03-19',
      panNo: 'AAGCI0729M',
      gstClassification: 'B2B Regular',
      gstin: '27AAGC10729M1ZB',
      msmeCategory: 'Micro',
      udyamRegistrationNo: 'UDYAM-MH-33-0194334',
      ieCode: 'AAGCI0729M',
      tan: 'PNEI10360A',
      companyType: 'IT'
    })
    setAddresses([{
      id: 1,
      country: '',
      stateProvince: '',
      cityDistrict: '',
      pinCode: '',
      addressLine1: '',
      addressLine2: '',
      addressLine3: '',
      addressLine4: ''
    }])
    setContacts([{
      id: 1,
      contactPerson: '',
      email: '',
      mobile: '',
      alternatePhone: '',
      department: '',
      designation: ''
    }])
    setBankDetails([{
      id: 1,
      beneficiaryName: 'IDMS INFOTECH PRIVATE LIMI',
      bankName: 'ICICI',
      bankBranch: 'RAM MARUTI ROAD, THANE',
      bankAddress: '',
      accountType: 'Current',
      accountNo: '003505016786',
      ifscCode: 'ICIC0000035',
      micrCode: '',
      exports: 'No',
      swiftCode: '',
      intermediaryBank: '',
      intermediaryBankSwiftCode: ''
    }])
    setTemplateData({
      purchaseOrderDomestics: 'PO Domestic',
      purchaseOrderImports: 'Regular POI',
      proformaInvoiceDomestic: 'PI Domestic',
      proformaInvoiceExports: 'PI Exports',
      taxInvoiceDomestics: 'Turnover less than 5 CR',
      taxInvoiceExports: 'Turnover less than 5 CR'
    })
    setDocumentData({
      companyLogo: '1721656049498-bezkoder-logo.svg',
      companyWelcomeInfo: '1721656049497-bezkoder-welcome',
      companySOSignature: '1722566773346-bezkoder-16851858',
      companyPOSignature: '1722566773345-bezkoder-16851858',
      companySOPDFHeader: '1722566773347-bezkoder-pdf_header',
      companyPOPDFHeader: '1722566773343-bezkoder-pdf_header'
    })
  }

  const handleBack = () => {
    navigate('/settings/global-master')
  }

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Settings',
      path: '/settings',
      onClick: () => navigate('/settings')
    },
    {
      label: 'Masters',
      path: '/settings/global-master',
      onClick: () => navigate('/settings/global-master')
    },
    {
      label: 'Company Master',
      path: '/settings/company-master',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Company Master Entry</h1>
          <p>Create and manage company information</p>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container">
        <div className="sac-form-header">
          <h2>Company Master Entry</h2>
        </div>
        
        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'companyInfo' ? 'active' : ''}`}
            onClick={() => setActiveTab('companyInfo')}
          >
            <Building size={18} />
            <span>Company Info</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'address' ? 'active' : ''}`}
            onClick={() => setActiveTab('address')}
          >
            <MapPin size={18} />
            <span>Address</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'contactDetails' ? 'active' : ''}`}
            onClick={() => setActiveTab('contactDetails')}
          >
            <Phone size={18} />
            <span>Contact Details</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'bankDetails' ? 'active' : ''}`}
            onClick={() => setActiveTab('bankDetails')}
          >
            <CreditCard size={18} />
            <span>Bank Details</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'template' ? 'active' : ''}`}
            onClick={() => setActiveTab('template')}
          >
            <FileText size={18} />
            <span>Template</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'documents' ? 'active' : ''}`}
            onClick={() => setActiveTab('documents')}
          >
            <Upload size={18} />
            <span>Documents</span>
          </button>
        </div>
          
        <form className="form-content">
          {loading && (
            <div className="loading-overlay">
              <div className="loading-spinner">Loading company details...</div>
            </div>
          )}
          {/* Company Info Tab */}
          {activeTab === 'companyInfo' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Company Name & Business Details</h3>
              </div>
              
              {/* Row 1: Company Name, Constitution, Corporate Identity, Date of Incorporation */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="companyName" className="form-label required">
                    Company Name [Legal Entity]
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="constitutionOfBusiness" className="form-label required">
                    Constitution of Business
                  </label>
                  <select
                    id="constitutionOfBusiness"
                    name="constitutionOfBusiness"
                    value={formData.constitutionOfBusiness}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="">Select Constitution</option>
                    <option value="Private Limited">Private Limited</option>
                    <option value="Public Limited">Public Limited</option>
                    <option value="Partnership">Partnership</option>
                    <option value="LLP">LLP</option>
                    <option value="Sole Proprietorship">Sole Proprietorship</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="corporateIdentityNo" className="form-label required">
                    Corporate Identity No.
                  </label>
                  <input
                    type="text"
                    id="corporateIdentityNo"
                    name="corporateIdentityNo"
                    value={formData.corporateIdentityNo}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="dateOfIncorporation" className="form-label required">
                    Date of Incorporation
                  </label>
                  <input
                    type="date"
                    id="dateOfIncorporation"
                    name="dateOfIncorporation"
                    value={formData.dateOfIncorporation}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>
              </div>

              {/* Row 2: Pan No, GST Classification, GSTIN, MSME Category */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="panNo" className="form-label required">
                    Pan No.
                  </label>
                  <input
                    type="text"
                    id="panNo"
                    name="panNo"
                    value={formData.panNo}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="gstClassification" className="form-label required">
                    GST Classification
                  </label>
                  <select
                    id="gstClassification"
                    name="gstClassification"
                    value={formData.gstClassification}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="">GST Classification</option>
                    <option value="B2B Regular">B2B Regular</option>
                    <option value="B2C Regular">B2C Regular</option>
                    <option value="B2B Composition">B2B Composition</option>
                    <option value="B2C Composition">B2C Composition</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="gstin" className="form-label required">
                    GSTIN
                  </label>
                  <input
                    type="text"
                    id="gstin"
                    name="gstin"
                    value={formData.gstin}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="msmeCategory" className="form-label required">
                    MSME Category
                  </label>
                  <select
                    id="msmeCategory"
                    name="msmeCategory"
                    value={formData.msmeCategory}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="">Select Category</option>
                    <option value="Micro">Micro</option>
                    <option value="Small">Small</option>
                    <option value="Medium">Medium</option>
                    <option value="Large">Large</option>
                  </select>
                </div>
              </div>

              {/* Row 3: Udyam Registration, IE Code, TAN, Company Type */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="udyamRegistrationNo" className="form-label required">
                    Udyam Registration No.
                  </label>
                  <input
                    type="text"
                    id="udyamRegistrationNo"
                    name="udyamRegistrationNo"
                    value={formData.udyamRegistrationNo}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="ieCode" className="form-label">
                    Import & export [IE] Code
                  </label>
                  <input
                    type="text"
                    id="ieCode"
                    name="ieCode"
                    value={formData.ieCode}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="tan" className="form-label required">
                    TAN[For TDS/TCS]
                  </label>
                  <input
                    type="text"
                    id="tan"
                    name="tan"
                    value={formData.tan}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="companyType" className="form-label required">
                    Company Type
                  </label>
                  <select
                    id="companyType"
                    name="companyType"
                    value={formData.companyType}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="">Select Type</option>
                    <option value="IT">IT</option>
                    <option value="Manufacturing">Manufacturing</option>
                    <option value="Trading">Trading</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Address Tab */}
          {activeTab === 'address' && (
            <div className="tab-content">
              <div className="form-section-header">
                <h3>Company Addresses</h3>
              </div>
              
              {addresses.map((address, index) => (
                <div key={address.id} className="address-section">
                  <div className="address-header">
                    <h3>
                      {index === 0 ? 'Primary Address' : `Address ${index + 1}`}
                    </h3>
                    {index === 0 && (
                      <button 
                        type="button" 
                        className="add-address-button"
                        onClick={addAddress}
                      >
                        <Plus size={16} />
                      </button>
                    )}
                    {index > 0 && (
                      <button 
                        type="button" 
                        className="remove-address-button"
                        onClick={() => removeAddress(address.id)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  
                  {/* Row 1: Country, State/Province, City/District, Pin Code */}
                  <div className="grid-row-4">
                    <div className="form-group">
                      <label className="form-label required">Country</label>
                      <select
                        value={address.country}
                        onChange={(e) => handleAddressChange(address.id, 'country', e.target.value)}
                        className="form-input"
                      >
                        <option value="">Select Country</option>
                        <option value="India">India</option>
                        <option value="USA">USA</option>
                        <option value="UK">UK</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label required">State/Province</label>
                      <select
                        value={address.stateProvince}
                        onChange={(e) => handleAddressChange(address.id, 'stateProvince', e.target.value)}
                        className="form-input"
                      >
                        <option value="">Select State</option>
                        {getStateOptions().map(state => (
                          <option key={state.value} value={state.value}>
                            {state.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label required">City/District</label>
                      <input
                        type="text"
                        value={address.cityDistrict}
                        onChange={(e) => handleAddressChange(address.id, 'cityDistrict', e.target.value)}
                        className="form-input"
                        placeholder="Enter city"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label required">Pin Code</label>
                      <input
                        type="text"
                        value={address.pinCode}
                        onChange={(e) => handleAddressChange(address.id, 'pinCode', e.target.value)}
                        className="form-input"
                        placeholder="Enter pin code"
                      />
                    </div>
                  </div>

                  {/* Row 2: Address Lines */}
                  <div className="grid-row-4">
                    <div className="form-group">
                      <label className="form-label required">Address Line 1</label>
                      <input
                        type="text"
                        value={address.addressLine1}
                        onChange={(e) => handleAddressChange(address.id, 'addressLine1', e.target.value)}
                        className="form-input"
                        placeholder="Enter address line 1"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label required">Address Line 2</label>
                      <input
                        type="text"
                        value={address.addressLine2}
                        onChange={(e) => handleAddressChange(address.id, 'addressLine2', e.target.value)}
                        className="form-input"
                        placeholder="Enter address line 2"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label required">Address Line 3</label>
                      <input
                        type="text"
                        value={address.addressLine3}
                        onChange={(e) => handleAddressChange(address.id, 'addressLine3', e.target.value)}
                        className="form-input"
                        placeholder="Enter address line 3"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Address Line 4</label>
                      <input
                        type="text"
                        value={address.addressLine4}
                        onChange={(e) => handleAddressChange(address.id, 'addressLine4', e.target.value)}
                        className="form-input"
                        placeholder="Enter address line 4"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Contact Details Tab */}
          {activeTab === 'contactDetails' && (
            <div className="tab-content">
              <div className="form-section-header">
                <h3>Contact Information</h3>
              </div>
              
              {contacts.map((contact, index) => (
                <div key={contact.id} className="contact-section">
                  <div className="contact-header">
                    <h3>
                      {index === 0 ? 'Primary Contact' : `Contact ${index + 1}`}
                    </h3>
                    {index === 0 && (
                      <button 
                        type="button" 
                        className="add-contact-button"
                        onClick={addContact}
                      >
                        <Plus size={16} />
                      </button>
                    )}
                    {index > 0 && (
                      <button 
                        type="button" 
                        className="remove-contact-button"
                        onClick={() => removeContact(contact.id)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  
                  {/* Row 1: Contact Person, Mobile, Email */}
                  <div className="grid-row-3">
                    <div className="form-group">
                      <label className="form-label required">Contact Person</label>
                      <input
                        type="text"
                        value={contact.contactPersonName}
                        onChange={(e) => handleContactChange(contact.id, 'contactPersonName', e.target.value)}
                        className="form-input"
                        placeholder="Enter contact person name"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label required">Mobile</label>
                      <input
                        type="tel"
                        value={contact.mobile}
                        onChange={(e) => handleContactChange(contact.id, 'mobile', e.target.value)}
                        className="form-input"
                        placeholder="Enter mobile number"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label required">Email</label>
                      <input
                        type="email"
                        value={contact.email}
                        onChange={(e) => handleContactChange(contact.id, 'email', e.target.value)}
                        className="form-input"
                        placeholder="Enter email address"
                      />
                    </div>
                  </div>

                  {/* Row 2: Alternate Phone, Department, Designation */}
                  <div className="grid-row-3">
                    <div className="form-group">
                      <label className="form-label">Alternate Phone</label>
                      <input
                        type="tel"
                        value={contact.alternatePhone}
                        onChange={(e) => handleContactChange(contact.id, 'alternatePhone', e.target.value)}
                        className="form-input"
                        placeholder="Enter alternate phone"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Department</label>
                      <input
                        type="text"
                        value={contact.department}
                        onChange={(e) => handleContactChange(contact.id, 'department', e.target.value)}
                        className="form-input"
                        placeholder="Enter department"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Designation</label>
                      <input
                        type="text"
                        value={contact.designation}
                        onChange={(e) => handleContactChange(contact.id, 'designation', e.target.value)}
                        className="form-input"
                        placeholder="Enter designation"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Bank Details Tab */}
          {activeTab === 'bankDetails' && (
            <div className="tab-content">
              <div className="form-section-header">
                <h3>Bank Details</h3>
              </div>
              
              {bankDetails.map((bank, index) => (
                <div key={bank.id} className="bank-section">
                  <div className="bank-header">
                    <h3>
                      {index === 0 ? 'Primary Bank' : `Bank ${index + 1}`}
                    </h3>
                    {index === 0 && (
                      <button 
                        type="button" 
                        className="add-bank-button"
                        onClick={addBank}
                      >
                        <Plus size={16} />
                      </button>
                    )}
                    {index > 0 && (
                      <button 
                        type="button" 
                        className="remove-bank-button"
                        onClick={() => removeBank(bank.id)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  
                  {/* Row 1: Beneficiary Name, Bank Name, Bank Branch, Bank Address */}
                  <div className="grid-row-4">
                    <div className="form-group">
                      <label className="form-label required">Beneficiary Name</label>
                      <input
                        type="text"
                        value={bank.beneficiaryName}
                        onChange={(e) => handleBankChange(bank.id, 'beneficiaryName', e.target.value)}
                        className="form-input"
                        placeholder="Enter beneficiary name"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label required">Bank Name</label>
                      <input
                        type="text"
                        value={bank.bankName}
                        onChange={(e) => handleBankChange(bank.id, 'bankName', e.target.value)}
                        className="form-input"
                        placeholder="Enter bank name"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label required">Bank Branch</label>
                      <input
                        type="text"
                        value={bank.bankBranch}
                        onChange={(e) => handleBankChange(bank.id, 'bankBranch', e.target.value)}
                        className="form-input"
                        placeholder="Enter bank branch"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Bank Address</label>
                      <input
                        type="text"
                        value={bank.bankAddress}
                        onChange={(e) => handleBankChange(bank.id, 'bankAddress', e.target.value)}
                        className="form-input"
                        placeholder="Enter bank address"
                      />
                    </div>
                  </div>

                  {/* Row 2: Account Type, Account No, IFSC Code, MICR Code */}
                  <div className="grid-row-4">
                    <div className="form-group">
                      <label className="form-label required">Account Type</label>
                      <select
                        value={bank.accountType}
                        onChange={(e) => handleBankChange(bank.id, 'accountType', e.target.value)}
                        className="form-input"
                      >
                        <option value="">Select Account Type</option>
                        <option value="Current">Current</option>
                        <option value="Savings">Savings</option>
                        <option value="Fixed Deposit">Fixed Deposit</option>
                        <option value="Recurring Deposit">Recurring Deposit</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label required">Account No.</label>
                      <input
                        type="text"
                        value={bank.accountNo}
                        onChange={(e) => handleBankChange(bank.id, 'accountNo', e.target.value)}
                        className="form-input"
                        placeholder="Enter account number"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label required">IFSC Code</label>
                      <input
                        type="text"
                        value={bank.ifscCode}
                        onChange={(e) => handleBankChange(bank.id, 'ifscCode', e.target.value)}
                        className="form-input"
                        placeholder="Enter IFSC code"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">MICR Code</label>
                      <input
                        type="text"
                        value={bank.micrCode}
                        onChange={(e) => handleBankChange(bank.id, 'micrCode', e.target.value)}
                        className="form-input"
                        placeholder="Enter MICR code"
                      />
                    </div>
                  </div>

                  {/* Row 3: Exports, Swift Code, Intermediary Bank, Intermediary Bank Swift Code */}
                  <div className="grid-row-4">
                    <div className="form-group">
                      <label className="form-label required">Exports</label>
                      <select
                        value={bank.exports}
                        onChange={(e) => handleBankChange(bank.id, 'exports', e.target.value)}
                        className="form-input"
                      >
                        <option value="No">No</option>
                        <option value="Yes">Yes</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Swift Code</label>
                      <input
                        type="text"
                        value={bank.swiftCode}
                        onChange={(e) => handleBankChange(bank.id, 'swiftCode', e.target.value)}
                        className="form-input"
                        placeholder="Enter Swift code"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Intermediary Bank</label>
                      <input
                        type="text"
                        value={bank.intermediaryBank}
                        onChange={(e) => handleBankChange(bank.id, 'intermediaryBank', e.target.value)}
                        className="form-input"
                        placeholder="Enter intermediary bank"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Intermediary Bank Swift Code</label>
                      <input
                        type="text"
                        value={bank.intermediaryBankSwiftCode}
                        onChange={(e) => handleBankChange(bank.id, 'intermediaryBankSwiftCode', e.target.value)}
                        className="form-input"
                        placeholder="Enter intermediary bank Swift code"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Template Tab */}
          {activeTab === 'template' && (
            <div className="tab-content">
              <div className="form-section-header">
                <h3>Template Details</h3>
              </div>
              
              {/* Row 1: Purchase Order Domestics, Purchase Order Imports, Proforma Invoice Domestic */}
              <div className="template-grid-row">
                <div className="form-group">
                  <label htmlFor="purchaseOrderDomestics" className="form-label required">
                    Purchase Order Domestics
                  </label>
                  <select
                    id="purchaseOrderDomestics"
                    name="purchaseOrderDomestics"
                    value={templateData.purchaseOrderDomestics}
                    onChange={handleTemplateChange}
                    className="form-input"
                  >
                    <option value="">Select Template</option>
                    <option value="PO Domestic">PO Domestic</option>
                    <option value="PO Domestic Alt">PO Domestic Alt</option>
                    <option value="PO Domestic Custom">PO Domestic Custom</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="purchaseOrderImports" className="form-label required">
                    Purchase Order Imports
                  </label>
                  <select
                    id="purchaseOrderImports"
                    name="purchaseOrderImports"
                    value={templateData.purchaseOrderImports}
                    onChange={handleTemplateChange}
                    className="form-input"
                  >
                    <option value="">Select Template</option>
                    <option value="Regular POI">Regular POI</option>
                    <option value="Standard POI">Standard POI</option>
                    <option value="Custom POI">Custom POI</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="proformaInvoiceDomestic" className="form-label required">
                    Proforma Invoice Domestic
                  </label>
                  <select
                    id="proformaInvoiceDomestic"
                    name="proformaInvoiceDomestic"
                    value={templateData.proformaInvoiceDomestic}
                    onChange={handleTemplateChange}
                    className="form-input"
                  >
                    <option value="">Select Template</option>
                    <option value="PI Domestic">PI Domestic</option>
                    <option value="PI Domestic Standard">PI Domestic Standard</option>
                    <option value="PI Domestic Custom">PI Domestic Custom</option>
                  </select>
                </div>
              </div>

              {/* Row 2: Proforma Invoice Exports, Tax Invoice Domestics, Tax Invoice Exports */}
              <div className="template-grid-row">
                <div className="form-group">
                  <label htmlFor="proformaInvoiceExports" className="form-label required">
                    Proforma Invoice Exports
                  </label>
                  <select
                    id="proformaInvoiceExports"
                    name="proformaInvoiceExports"
                    value={templateData.proformaInvoiceExports}
                    onChange={handleTemplateChange}
                    className="form-input"
                  >
                    <option value="">Select Template</option>
                    <option value="PI Exports">PI Exports</option>
                    <option value="PI Exports Standard">PI Exports Standard</option>
                    <option value="PI Exports Custom">PI Exports Custom</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="taxInvoiceDomestics" className="form-label required">
                    Tax Invoice Domestics
                  </label>
                  <select
                    id="taxInvoiceDomestics"
                    name="taxInvoiceDomestics"
                    value={templateData.taxInvoiceDomestics}
                    onChange={handleTemplateChange}
                    className="form-input"
                  >
                    <option value="">Select Template</option>
                    <option value="Turnover less than 5 CR">Turnover less than 5 CR</option>
                    <option value="Turnover more than 5 CR">Turnover more than 5 CR</option>
                    <option value="Custom Tax Invoice">Custom Tax Invoice</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="taxInvoiceExports" className="form-label required">
                    Tax Invoice Exports
                  </label>
                  <select
                    id="taxInvoiceExports"
                    name="taxInvoiceExports"
                    value={templateData.taxInvoiceExports}
                    onChange={handleTemplateChange}
                    className="form-input"
                  >
                    <option value="">Select Template</option>
                    <option value="Turnover less than 5 CR">Turnover less than 5 CR</option>
                    <option value="Turnover more than 5 CR">Turnover more than 5 CR</option>
                    <option value="Custom Tax Invoice">Custom Tax Invoice</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div className="tab-content">
              <div className="form-section-header">
                <h3>Document Details</h3>
              </div>
              
              {/* Row 1: Company Logo, Company Welcome Info */}
              <div className="document-grid-row">
                <div className="document-section">
                  <div className="document-header">
                    <h4>Company Logo</h4>
                  </div>
                  <div className="document-content">
                    <input
                      type="text"
                      value={documentData.companyLogo}
                      onChange={(e) => handleDocumentChange(e)}
                      name="companyLogo"
                      className="form-input"
                      readOnly
                    />
                    <div className="document-actions">
                      <button
                        type="button"
                        className="btn-select-file"
                        onClick={() => handleFileSelect('companyLogo')}
                      >
                        Select File
                      </button>
                      <button
                        type="button"
                        className="btn-view-file"
                        onClick={() => handleViewFile('companyLogo')}
                      >
                        View
                      </button>
                    </div>
                  </div>
                </div>

                <div className="document-section">
                  <div className="document-header">
                    <h4>Company Welcome Info</h4>
                  </div>
                  <div className="document-content">
                    <input
                      type="text"
                      value={documentData.companyWelcomeInfo}
                      onChange={(e) => handleDocumentChange(e)}
                      name="companyWelcomeInfo"
                      className="form-input"
                      readOnly
                    />
                    <div className="document-actions">
                      <button
                        type="button"
                        className="btn-select-file"
                        onClick={() => handleFileSelect('companyWelcomeInfo')}
                      >
                        Select File
                      </button>
                      <button
                        type="button"
                        className="btn-view-file"
                        onClick={() => handleViewFile('companyWelcomeInfo')}
                      >
                        View
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 2: Company SO Signature, Company PO Signature */}
              <div className="document-grid-row">
                <div className="document-section">
                  <div className="document-header">
                    <h4>Company SO Signature</h4>
                  </div>
                  <div className="document-content">
                    <input
                      type="text"
                      value={documentData.companySOSignature}
                      onChange={(e) => handleDocumentChange(e)}
                      name="companySOSignature"
                      className="form-input"
                      readOnly
                    />
                    <div className="document-actions">
                      <button
                        type="button"
                        className="btn-select-file"
                        onClick={() => handleFileSelect('companySOSignature')}
                      >
                        Select File
                      </button>
                      <button
                        type="button"
                        className="btn-view-file"
                        onClick={() => handleViewFile('companySOSignature')}
                      >
                        View
                      </button>
                    </div>
                  </div>
                </div>

                <div className="document-section">
                  <div className="document-header">
                    <h4>Company PO Signature</h4>
                  </div>
                  <div className="document-content">
                    <input
                      type="text"
                      value={documentData.companyPOSignature}
                      onChange={(e) => handleDocumentChange(e)}
                      name="companyPOSignature"
                      className="form-input"
                      readOnly
                    />
                    <div className="document-actions">
                      <button
                        type="button"
                        className="btn-select-file"
                        onClick={() => handleFileSelect('companyPOSignature')}
                      >
                        Select File
                      </button>
                      <button
                        type="button"
                        className="btn-view-file"
                        onClick={() => handleViewFile('companyPOSignature')}
                      >
                        View
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 3: Company SO PDF Header, Company PO PDF Header */}
              <div className="document-grid-row">
                <div className="document-section">
                  <div className="document-header">
                    <h4>Company SO PDF Header</h4>
                  </div>
                  <div className="document-content">
                    <input
                      type="text"
                      value={documentData.companySOPDFHeader}
                      onChange={(e) => handleDocumentChange(e)}
                      name="companySOPDFHeader"
                      className="form-input"
                      readOnly
                    />
                    <div className="document-actions">
                      <button
                        type="button"
                        className="btn-select-file"
                        onClick={() => handleFileSelect('companySOPDFHeader')}
                      >
                        Select File
                      </button>
                      <button
                        type="button"
                        className="btn-view-file"
                        onClick={() => handleViewFile('companySOPDFHeader')}
                      >
                        View
                      </button>
                    </div>
                  </div>
                </div>

                <div className="document-section">
                  <div className="document-header">
                    <h4>Company PO PDF Header</h4>
                  </div>
                  <div className="document-content">
                    <input
                      type="text"
                      value={documentData.companyPOPDFHeader}
                      onChange={(e) => handleDocumentChange(e)}
                      name="companyPOPDFHeader"
                      className="form-input"
                      readOnly
                    />
                    <div className="document-actions">
                      <button
                        type="button"
                        className="btn-select-file"
                        onClick={() => handleFileSelect('companyPOPDFHeader')}
                      >
                        Select File
                      </button>
                      <button
                        type="button"
                        className="btn-view-file"
                        onClick={() => handleViewFile('companyPOPDFHeader')}
                      >
                        View
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}


          {/* Action Buttons - Available in all tabs */}
          <div className="form-actions">
            <button
              type="button"
              onClick={handleReset}
              className="btn-secondary"
              disabled={loading || saving}
            >
              <RotateCcw size={16} />
              Reset
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="btn-primary"
              disabled={loading || saving}
            >
              <Save size={16} />
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CompanyMaster